/**
 * Unbilled Charges "Summary" pivot: categories across the top, a selectable
 * row axis (region / responsible party / LC reason / CSR) down the side, each
 * row expandable into its sub-level split. Fed by GET /unbilled-charges/matrix
 * (flat axis x sub x category rows); the pivot happens here. Cells show the
 * unbilled amount + charge count, and — given `onCellClick` — are links that
 * hand the page the cell's coordinates so it can open the rows behind it.
 */
import React, { useMemo, useState } from 'react';
import ExcelJS from 'exceljs';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { ArrowDownTrayIcon, ArrowLongDownIcon, ArrowLongUpIcon } from '@heroicons/react/24/outline';
import { saveBlob } from '../../../utils/saveFile';
import { toLocalDateKey } from '../../../utils/dateUtils';

export interface MatrixDatum {
  axis_value: string;
  sub_value: string;
  category: string;
  row_count: number;
  total_cost: number;
  total_revenue: number;
  total_margin: number;
}

interface Cell {
  count: number;
  margin: number;
}

/**
 * Where a clicked cell sits in the pivot. Values are the backend's raw axis /
 * sub values ('' = the blank/uncoded bucket), labels are what the table showed;
 * `category` is undefined for the Total column and `subValue` for an axis
 * (top-level) row.
 */
export interface MatrixCellRef {
  axisValue: string;
  axisLabel: string;
  subValue?: string;
  subLabel?: string;
  category?: string;
  categoryLabel?: string;
  amount: number;
  count: number;
}

interface MatrixTableProps {
  data: MatrixDatum[];
  categories: Array<{ key: string; label: string }>;
  /** First-column header, e.g. "Region / Responsible". */
  axisHeader: string;
  /** Label for blank axis values (e.g. "(no region)" / "(Not coded)"). */
  blankAxisLabel: string;
  /** Label for blank sub values. */
  blankSubLabel: string;
  /** Render the expandable sub-level split (default true). When false the
   * table is flat — axis rows only, no chevrons/expansion, and the Excel
   * export skips the sub rows too. */
  showSubRows?: boolean;
  /** Makes every non-empty amount a link; called with the cell's coordinates. */
  onCellClick?: (cell: MatrixCellRef) => void;
  loading?: boolean;
}

const fmtMoney = (v: number): string =>
  v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/** Underline accent per category column header. */
const CATEGORY_ACCENTS: Record<string, string> = {
  bobtail: 'bg-blue-500',
  waiting_time: 'bg-purple-500',
  hazmat: 'bg-orange-400',
  chassis_split: 'bg-teal-500',
  storage: 'bg-pink-500',
  per_diem: 'bg-gray-400',
  ssl_chassis: 'bg-indigo-500',
  pre_pull: 'bg-cyan-500',
  stop_off: 'bg-red-500',
};

/** Pastel family per region group — confined to the name pill in the first
 * column (the data cells stay white); cycled by group index. */
const GROUP_COLORS = [
  { band: 'bg-blue-50 dark:bg-blue-900/25', sub: 'bg-blue-50 dark:bg-blue-900/20', accent: 'border-l-blue-500', chevron: 'text-blue-600 dark:text-blue-400', dot: 'bg-blue-500' },
  { band: 'bg-green-50 dark:bg-green-900/25', sub: 'bg-green-50 dark:bg-green-900/20', accent: 'border-l-green-500', chevron: 'text-green-600 dark:text-green-400', dot: 'bg-green-500' },
  { band: 'bg-purple-50 dark:bg-purple-900/25', sub: 'bg-purple-50 dark:bg-purple-900/20', accent: 'border-l-purple-500', chevron: 'text-purple-600 dark:text-purple-400', dot: 'bg-purple-500' },
  { band: 'bg-orange-50 dark:bg-orange-900/25', sub: 'bg-orange-50 dark:bg-orange-900/20', accent: 'border-l-orange-500', chevron: 'text-orange-600 dark:text-orange-400', dot: 'bg-orange-500' },
  { band: 'bg-teal-50 dark:bg-teal-900/25', sub: 'bg-teal-50 dark:bg-teal-900/20', accent: 'border-l-teal-500', chevron: 'text-teal-600 dark:text-teal-400', dot: 'bg-teal-500' },
  { band: 'bg-amber-50 dark:bg-amber-900/25', sub: 'bg-amber-50 dark:bg-amber-900/20', accent: 'border-l-amber-500', chevron: 'text-amber-600 dark:text-amber-400', dot: 'bg-amber-500' },
  { band: 'bg-sky-50 dark:bg-sky-900/25', sub: 'bg-sky-50 dark:bg-sky-900/20', accent: 'border-l-sky-500', chevron: 'text-sky-600 dark:text-sky-400', dot: 'bg-sky-500' },
  { band: 'bg-pink-50 dark:bg-pink-900/25', sub: 'bg-pink-50 dark:bg-pink-900/20', accent: 'border-l-pink-500', chevron: 'text-pink-600 dark:text-pink-400', dot: 'bg-pink-500' },
];

const addTo = (bucket: Record<string, Cell>, key: string, d: MatrixDatum) => {
  const cell = bucket[key] ?? (bucket[key] = { count: 0, margin: 0 });
  cell.count += d.row_count;
  cell.margin += d.total_margin;
};

// Every cell is a loss by definition (the page only shows cost > revenue), so
// the sign and alarm-red are noise — show the unbilled amount plainly. With an
// onClick the amount is a link (a button — the row itself toggles expansion on
// click, so the click must not bubble).
const CellView: React.FC<{
  cell?: Cell;
  onDark?: boolean;
  muted?: boolean;
  onClick?: () => void;
  label?: string;
}> = ({ cell, onDark, muted, onClick, label }) => {
  if (!cell) {
    return <div className={`text-right ${onDark ? 'text-blue-200' : 'text-gray-300 dark:text-gray-600'}`}>—</div>;
  }
  const charges = `${cell.count} charge${cell.count === 1 ? '' : 's'}`;
  const amount = fmtMoney(Math.abs(cell.margin));
  if (onClick) {
    return (
      <div className="text-right leading-tight">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClick(); }}
          title={`${charges} — click to see them`}
          aria-label={`${label ? `${label}: ` : ''}${amount}, ${charges}`}
          className={`tabular-nums hover:underline ${
            muted
              ? 'italic text-blue-500/80 hover:text-blue-700 dark:text-blue-300/80 dark:hover:text-blue-200'
              : 'text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'
          }`}
        >
          {amount}
        </button>
      </div>
    );
  }
  return (
    <div className="text-right leading-tight" title={charges}>
      <div className={`${onDark ? 'text-white font-semibold' : muted ? 'italic text-muted' : 'text-gray-800 dark:text-gray-200'}`}>
        {amount}
      </div>
    </div>
  );
};

export const MatrixTable: React.FC<MatrixTableProps> = ({ data, categories, axisHeader, blankAxisLabel, blankSubLabel, showSubRows = true, onCellClick, loading }) => {
  // Expanded by default — the three-tier styling (region band / departments /
  // catch-all) is the whole point of the view. Tracks *collapsed* rows.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // Top-level sort: by axis name, the displayed Total, or a category column's
  // displayed amount (all |margin|; rows without the category count as 0),
  // toggled by clicking the headers. Default = biggest Total first (the
  // original order). Catch-all "(...)" buckets stay pinned last regardless.
  // Keys: 'name' | 'total' | a category key (category keys can't collide —
  // they're snake_case backend identifiers).
  const [sort, setSort] = useState<{ key: string; desc: boolean }>({ key: 'total', desc: true });
  const toggleSort = (key: string) =>
    setSort((prev) => prev.key === key
      ? { key, desc: !prev.desc }
      : { key, desc: key !== 'name' });  // fresh sorts: names A→Z, amounts biggest-first
  const sortIndicator = (key: string) =>
    sort.key === key
      ? (sort.desc
        ? <ArrowLongDownIcon className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
        : <ArrowLongUpIcon className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />)
      : null;

  const { regions, grand } = useMemo(() => {
    // region -> { raw, perCat, total, resp: responsible -> { raw, perCat, total } }
    // `raw` keeps the backend's own value ('' for the blank bucket) next to the
    // display name, so a click can be sent back as the coordinates it came from.
    const regionMap = new Map<string, {
      raw: string;
      perCat: Record<string, Cell>;
      total: Cell;
      resp: Map<string, { raw: string; perCat: Record<string, Cell>; total: Cell }>;
    }>();
    const grandPerCat: Record<string, Cell> = {};
    const grandTotal: Cell = { count: 0, margin: 0 };

    for (const d of data) {
      const regionKey = d.axis_value || blankAxisLabel;
      let region = regionMap.get(regionKey);
      if (!region) {
        region = { raw: d.axis_value, perCat: {}, total: { count: 0, margin: 0 }, resp: new Map() };
        regionMap.set(regionKey, region);
      }
      addTo(region.perCat, d.category, d);
      region.total.count += d.row_count;
      region.total.margin += d.total_margin;

      const respKey = d.sub_value || blankSubLabel;
      let resp = region.resp.get(respKey);
      if (!resp) {
        resp = { raw: d.sub_value, perCat: {}, total: { count: 0, margin: 0 } };
        region.resp.set(respKey, resp);
      }
      addTo(resp.perCat, d.category, d);
      resp.total.count += d.row_count;
      resp.total.margin += d.total_margin;

      addTo(grandPerCat, d.category, d);
      grandTotal.count += d.row_count;
      grandTotal.margin += d.total_margin;
    }

    // Sub-rows: biggest loss first, but catch-all buckets — "(Not coded)" /
    // "(no region)" — always sort last within their level, never interleaved.
    const bucketSort = (
      [nameA, a]: [string, { total: Cell }],
      [nameB, b]: [string, { total: Cell }],
    ) =>
      (nameA.startsWith('(') ? 1 : 0) - (nameB.startsWith('(') ? 1 : 0) ||
      a.total.margin - b.total.margin;

    // Top level follows the header-click sort (name, or a displayed |margin|
    // amount — Total or one category's); the catch-all-last rule still
    // outranks it.
    const amountFor = (r: { perCat: Record<string, Cell>; total: Cell }) =>
      sort.key === 'total'
        ? Math.abs(r.total.margin)
        : Math.abs(r.perCat[sort.key]?.margin ?? 0);
    const topSort = (
      [nameA, a]: [string, { perCat: Record<string, Cell>; total: Cell }],
      [nameB, b]: [string, { perCat: Record<string, Cell>; total: Cell }],
    ) => {
      const bucket = (nameA.startsWith('(') ? 1 : 0) - (nameB.startsWith('(') ? 1 : 0);
      if (bucket !== 0) return bucket;
      const cmp = sort.key === 'name'
        ? nameA.localeCompare(nameB)
        : amountFor(a) - amountFor(b);
      return sort.desc ? -cmp : cmp;
    };

    const regions = Array.from(regionMap.entries())
      .sort(topSort)
      .map(([name, r]) => ({
        name,
        ...r,
        respRows: Array.from(r.resp.entries())
          .sort(bucketSort)
          .map(([respName, v]) => ({ name: respName, ...v })),
      }));
    return { regions, grand: { perCat: grandPerCat, total: grandTotal } };
  }, [data, blankAxisLabel, blankSubLabel, sort]);

  if (loading) {
    return <div className="p-8 text-center text-sm text-muted">Loading…</div>;
  }
  if (regions.length === 0) {
    return <div className="p-8 text-center text-sm text-muted">No unbilled charges in this window.</div>;
  }

  const headerCell = 'px-3 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 whitespace-nowrap';
  const bodyCell = 'px-3 py-2.5 text-sm align-top';

  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Unbilled Charges Summary');
    sheet.columns = [
      { header: axisHeader, key: 'name', width: 32 },
      ...categories.map(c => ({ header: c.label, key: c.key, width: 16 })),
      { header: 'Total', key: 'total', width: 16 },
      { header: 'Charges', key: 'charges', width: 10 },
    ];
    sheet.getRow(1).font = { bold: true };
    const moneyCols = [...categories.map(c => c.key), 'total'];
    const addRow = (name: string, perCat: Record<string, Cell>, total: Cell,
                    opts: { bold?: boolean; indent?: boolean } = {}) => {
      const row = sheet.addRow({
        name: opts.indent ? `    ${name}` : name,
        ...Object.fromEntries(categories.map(c =>
          [c.key, perCat[c.key] ? Math.abs(perCat[c.key].margin) : null])),
        total: Math.abs(total.margin),
        charges: total.count,
      });
      if (opts.bold) row.font = { bold: true };
      moneyCols.forEach(key => { row.getCell(key).numFmt = '$#,##0.00'; });
      return row;
    };
    for (const region of regions) {
      addRow(region.name, region.perCat, region.total, { bold: true });
      if (showSubRows) {
        for (const resp of region.respRows) {
          addRow(resp.name, resp.perCat, resp.total, { indent: true });
        }
      }
    }
    addRow('Total', grand.perCat, grand.total, { bold: true });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    // Local date — toISOString() is UTC and names tomorrow after 7 pm Central.
    await saveBlob(blob, `unbilled-charges-summary-${toLocalDateKey()}.xlsx`);
  };

  const allExpanded = regions.every(r => !collapsed[r.name]);
  const toggleAll = () =>
    setCollapsed(allExpanded ? Object.fromEntries(regions.map(r => [r.name, true])) : {});

  // Click handlers per cell, or undefined (plain text) when the page didn't ask.
  const clickFor = (
    cell: Cell | undefined,
    region: { name: string; raw: string },
    resp: { name: string; raw: string } | null,
    category: { key: string; label: string } | null,
  ): (() => void) | undefined => {
    if (!onCellClick || !cell) return undefined;
    return () => onCellClick({
      axisValue: region.raw,
      axisLabel: region.name,
      subValue: resp ? resp.raw : undefined,
      subLabel: resp ? resp.name : undefined,
      category: category?.key,
      categoryLabel: category?.label,
      amount: Math.abs(cell.margin),
      count: cell.count,
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        {showSubRows ? (
          <button
            type="button"
            onClick={toggleAll}
            className="px-2 py-0.5 text-xs font-normal text-gray-600 bg-white border border-slate-300 rounded hover:bg-slate-50 dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600"
          >
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={exportToExcel}
          title="Export to Excel (all rows, expanded)"
          className="px-2 py-0.5 text-xs font-normal text-gray-600 bg-white border border-slate-300 rounded hover:bg-slate-50 dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 inline-flex items-center gap-1"
        >
          <ArrowDownTrayIcon className="h-3.5 w-3.5" />
          Excel
        </button>
      </div>
      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg bg-panel">
      <table className="min-w-full">
        <thead className="bg-panel border-b border-gray-200 dark:border-gray-700">
          <tr className="border-l-4 border-l-transparent">
            <th
              className={`${headerCell} text-left sticky left-0 bg-white dark:bg-gray-800 cursor-pointer select-none`}
              onClick={() => toggleSort('name')}
              title="Sort by name"
              aria-sort={sort.key === 'name' ? (sort.desc ? 'descending' : 'ascending') : 'none'}
            >
              <span className="inline-flex items-center gap-1">
                {axisHeader}
                {sortIndicator('name')}
              </span>
            </th>
            {categories.map(c => (
              <th
                key={c.key}
                className={`${headerCell} text-center cursor-pointer select-none`}
                onClick={() => toggleSort(c.key)}
                title={`Sort by ${c.label}`}
                aria-sort={sort.key === c.key ? (sort.desc ? 'descending' : 'ascending') : 'none'}
              >
                <div className="flex flex-col items-stretch">
                  <span className="inline-flex items-center justify-center gap-1">
                    {c.label}
                    {sortIndicator(c.key)}
                  </span>
                  <span className={`h-1 w-full rounded-full mt-1.5 ${CATEGORY_ACCENTS[c.key] ?? 'bg-gray-300'}`} />
                </div>
              </th>
            ))}
            <th
              className={`${headerCell} text-right border-l border-gray-200 dark:border-gray-700 cursor-pointer select-none`}
              onClick={() => toggleSort('total')}
              title="Sort by total"
              aria-sort={sort.key === 'total' ? (sort.desc ? 'descending' : 'ascending') : 'none'}
            >
              <span className="inline-flex items-center justify-end gap-1">
                {sortIndicator('total')}
                Total
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {regions.map((region, gi) => {
            const colors = GROUP_COLORS[gi % GROUP_COLORS.length];
            return (
            <React.Fragment key={region.name}>
              <tr
                className={`border-l-4 ${colors.accent} ${colors.band} border-b border-gray-200 dark:border-gray-700 ${showSubRows ? 'cursor-pointer hover:brightness-95 dark:hover:brightness-110' : ''}`}
                onClick={showSubRows ? () => setCollapsed(prev => ({ ...prev, [region.name]: !prev[region.name] })) : undefined}
              >
                <td className={`${bodyCell} font-semibold sticky left-0 bg-inherit whitespace-nowrap ${region.name.startsWith('(') ? 'italic text-muted' : 'text-heading'}`}>
                  <span className="inline-flex items-center gap-2">
                    {showSubRows && (!collapsed[region.name]
                      ? <ChevronDownIcon className={`h-4 w-4 ${colors.chevron}`} />
                      : <ChevronRightIcon className={`h-4 w-4 ${colors.chevron}`} />)}
                    {region.name}
                  </span>
                </td>
                {categories.map(c => (
                  <td key={c.key} className={bodyCell}>
                    <CellView
                      cell={region.perCat[c.key]}
                      onClick={clickFor(region.perCat[c.key], region, null, c)}
                      label={`${region.name} ${c.label}`}
                    />
                  </td>
                ))}
                <td className={`${bodyCell} border-l border-gray-200 dark:border-gray-700 font-semibold`}>
                  <CellView
                    cell={region.total}
                    onClick={clickFor(region.total, region, null, null)}
                    label={`${region.name} Total`}
                  />
                </td>
              </tr>
              {showSubRows && !collapsed[region.name] && region.respRows.map(resp => (
                <tr key={`${region.name}|${resp.name}`} className="border-l-4 border-l-transparent border-b border-gray-100 dark:border-gray-700/40 bg-panel hover:bg-blue-50/60 dark:hover:bg-gray-700/40">
                  <td className={`${bodyCell} pl-10 sticky left-0 bg-inherit whitespace-nowrap ${resp.name.startsWith('(') ? 'italic text-gray-400 dark:text-gray-500' : 'text-label'}`}>
                    <span className="inline-flex items-center gap-2">
                      <span className={`inline-block h-1.5 w-1.5 rounded-full ${colors.dot}`} />
                      {resp.name}
                    </span>
                  </td>
                  {categories.map(c => (
                    <td key={c.key} className={bodyCell}>
                      <CellView
                        cell={resp.perCat[c.key]}
                        muted={resp.name.startsWith('(')}
                        onClick={clickFor(resp.perCat[c.key], region, resp, c)}
                        label={`${region.name} / ${resp.name} ${c.label}`}
                      />
                    </td>
                  ))}
                  <td className={`${bodyCell} border-l border-gray-200 dark:border-gray-700`}>
                    <CellView
                      cell={resp.total}
                      muted={resp.name.startsWith('(')}
                      onClick={clickFor(resp.total, region, resp, null)}
                      label={`${region.name} / ${resp.name} Total`}
                    />
                  </td>
                </tr>
              ))}
            </React.Fragment>
            );
          })}
        </tbody>
        <tfoot className="border-t border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-900/30">
          <tr className="border-l-4 border-l-blue-50 dark:border-l-blue-950">
            <td className={`${bodyCell} font-semibold text-gray-900 dark:text-white sticky left-0 bg-inherit`}>Total</td>
            {categories.map(c => (
              <td key={c.key} className={`${bodyCell} font-semibold`}><CellView cell={grand.perCat[c.key]} /></td>
            ))}
            <td className={`${bodyCell} border-l border-blue-200 dark:border-blue-900 font-semibold`}>
              <CellView cell={grand.total} />
            </td>
          </tr>
        </tfoot>
      </table>
      </div>
    </div>
  );
};

export default MatrixTable;
